import { awsClient } from './awsClient';

const contentBasePath = '/content';

function joinBaseUrl(basePath, assetPath) {
  if (!assetPath) return null;
  if (/^https?:\/\//i.test(assetPath)) return assetPath;

  const normalizedBase = (basePath || '/').replace(/\/+$/, '');
  const normalizedAsset = String(assetPath).replace(/^\/+/, '');

  return normalizedBase ? `${normalizedBase}/${normalizedAsset}` : `/${normalizedAsset}`;
}

function resolveMediaUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;

  const mediaBaseUrl = import.meta.env.VITE_MEDIA_BASE_URL;
  if (!mediaBaseUrl) {
    return null;
  }

  return joinBaseUrl(mediaBaseUrl, path);
}

/**
 * DB の materials:
 * - JSON 配列 `[{ name, url }]` または `["study/a.jpg"]`
 * - JSON 文字列（上と同じ）
 * - 単一の S3 キー文字列 `study/video-1.jpg`（VARCHAR 1 セル）
 * 相対キーは `VITE_MEDIA_BASE_URL` で解決。
 */
function normalizeMaterials(rawMaterials) {
  if (rawMaterials == null || rawMaterials === '') return [];

  let list = rawMaterials;

  if (typeof rawMaterials === 'string') {
    const trimmed = rawMaterials.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        list = JSON.parse(trimmed);
      } catch {
        list = [{ name: '', url: trimmed }];
      }
    } else {
      list = [{ name: '', url: trimmed }];
    }
  }

  if (list != null && typeof list === 'object' && !Array.isArray(list)) {
    list = [list];
  }

  if (!Array.isArray(list)) return [];

  return list
    .map((entry) => {
      const m = typeof entry === 'string' ? { name: '', url: entry } : entry;
      if (!m || typeof m !== 'object') return null;

      const pathOrUrl =
        m.url ??
        m.object_url ??
        m.objectUrl ??
        m.object_key ??
        m.objectKey ??
        m.s3_key ??
        m.s3Key ??
        null;
      if (!pathOrUrl) return null;

      const resolved = /^https?:\/\//i.test(String(pathOrUrl))
        ? String(pathOrUrl)
        : resolveMediaUrl(pathOrUrl);
      if (!resolved) return null;

      const name =
        (m.name ?? m.title ?? '').trim() ||
        String(pathOrUrl).replace(/^.*[/\\]/, '') ||
        'Download';

      return { ...m, name, url: resolved };
    })
    .filter(Boolean);
}

/**
 * DB の `videos.questionnaire_url`: 未設定の動画は NULL のため `null` に揃える。
 * リンクとして開くのは `http(s)` のみ許可する。
 */
function normalizeQuestionnaireUrl(rawUrl) {
  if (rawUrl == null) return null;

  const trimmed = String(rawUrl).trim();
  if (!trimmed) return null;

  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toBoolean(value) {
  return value === true || value === 1 || value === '1';
}

function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeNavigationVideo(video) {
  if (!video) return null;

  return {
    id: video.id ?? null,
    slug: video.slug ?? null,
    title: video.title ?? null,
  };
}

/** S3 キー（DB の snake / camel）。新カラムのみ参照。 */
function collectS3FormatKeys(rawVideo) {
  const mp4 =
    rawVideo?.s3_object_key_mp4 ??
    rawVideo?.s3ObjectKeyMp4 ?? null;
  const webm =
    rawVideo?.s3_object_key_webm ?? rawVideo?.s3ObjectKeyWebm ?? null;
  return { mp4, webm };
}

/** WebM を先に並べ、video 要素の複数 source 用（未設定は省略）。 */
function buildProgressiveSources(formatKeys) {
  const { mp4, webm } = formatKeys;
  const sources = [];
  const urlWebm = resolveMediaUrl(webm);
  const urlMp4 = resolveMediaUrl(mp4);
  if (urlWebm) sources.push({ type: 'video/webm', url: urlWebm });
  if (urlMp4) sources.push({ type: 'video/mp4', url: urlMp4 });
  return sources;
}

/**
 * API は `{ id, name, slug, isActive }` を返すが、slug の配列だけの旧レスポンスも受ける。
 * 消費側（Catalog / VideoCard / VideoPlayer など）は `entry?.slug` を読むので、
 * オブジェクトに揃えても表示・絞り込みの挙動は変わらない。
 */
function normalizeVideoTags(rawTags) {
  if (!Array.isArray(rawTags)) return [];

  return rawTags
    .map((entry) => {
      if (typeof entry === 'string') {
        return { id: null, name: entry, slug: entry, isActive: true };
      }
      const slug = entry?.slug ?? null;
      return {
        id: entry?.id ?? null,
        name: entry?.name ?? slug ?? '',
        slug,
        isActive: entry?.isActive !== false,
      };
    })
    .filter((tag) => tag.slug || tag.name);
}

export function normalizeVideo(rawVideo) {
  const durationSeconds = toNumber(
    rawVideo?.durationSeconds ?? rawVideo?.duration_seconds,
    0
  );
  const durationMinutes = toNumber(
    rawVideo?.duration_minutes,
    durationSeconds > 0 ? Math.max(1, Math.ceil(durationSeconds / 60)) : 0
  );
  const thumbnailPath =
    rawVideo?.thumbnailUrl ??
    rawVideo?.thumbnail_url ??
    rawVideo?.thumbnail_object_key ??
    null;

  const formatKeys = collectS3FormatKeys(rawVideo);
  const progressiveSources = buildProgressiveSources(formatKeys);
  const primaryFromKeys = progressiveSources[0]?.url ?? null;

  const explicitPlaybackUrl = rawVideo?.playback?.url ?? null;
  const explicitPlaybackSources = Array.isArray(rawVideo?.playback?.sources)
    ? rawVideo.playback.sources
        .map((s) => ({
          type: s?.type ?? null,
          url: resolveMediaUrl(s?.url ?? null),
        }))
        .filter((s) => s.type && s.url)
    : [];
  const fallbackPlaybackUrl =
    rawVideo?.playbackUrl ??
    rawVideo?.video_url ??
    rawVideo?.videoUrl ??
    rawVideo?.s3_url ??
    primaryFromKeys ??
    null;

  return {
    id: rawVideo?.id,
    public_id: rawVideo?.publicId ?? rawVideo?.public_id ?? null,
    category_id: rawVideo?.categoryId ?? rawVideo?.category_id ?? null,
    series_id: rawVideo?.seriesId ?? rawVideo?.series_id ?? null,
    title: rawVideo?.title ?? '',
    slug: rawVideo?.slug ?? null,
    description: rawVideo?.description ?? '',
    lesson_no: toNumber(rawVideo?.lessonNo ?? rawVideo?.lesson_no, 0),
    sort_order: toNumber(rawVideo?.sortOrder ?? rawVideo?.sort_order, 0),
    order_in_series: toNumber(
      rawVideo?.order_in_series ?? rawVideo?.sortOrder ?? rawVideo?.sort_order ?? rawVideo?.lessonNo ?? rawVideo?.lesson_no,
      0
    ),
    storage_type: rawVideo?.storageType ?? rawVideo?.storage_type ?? 's3',
    duration_seconds: durationSeconds,
    duration_minutes: durationMinutes,
    thumbnail_url: resolveMediaUrl(thumbnailPath),
    thumbnail_object_key: rawVideo?.thumbnail_object_key ?? thumbnailPath,
    s3_object_key_mp4: formatKeys.mp4 ?? null,
    s3_object_key_webm: formatKeys.webm ?? null,
    video_url: resolveMediaUrl(explicitPlaybackUrl ?? fallbackPlaybackUrl),
    view_count: toNumber(rawVideo?.viewCount ?? rawVideo?.view_count, 0),
    // 公開側の一覧は公開済みだけが返るため、未指定なら公開扱いにする（admin一覧だけが0を受け取る）
    is_published: toBoolean(rawVideo?.isPublished ?? rawVideo?.is_published ?? true),
    created_at: rawVideo?.createdAt ?? rawVideo?.created_at ?? null,
    updated_at: rawVideo?.updatedAt ?? rawVideo?.updated_at ?? null,
    created_date: rawVideo?.createdAt ?? rawVideo?.created_at ?? null,
    genre: rawVideo?.genre ?? null,
    tags: normalizeVideoTags(rawVideo?.tags),
    is_free: toBoolean(rawVideo?.isPreview ?? rawVideo?.is_preview),
    is_preview: toBoolean(rawVideo?.isPreview ?? rawVideo?.is_preview),
    materials: normalizeMaterials(rawVideo?.materials),
    questionnaire_url: normalizeQuestionnaireUrl(
      rawVideo?.questionnaireUrl ?? rawVideo?.questionnaire_url
    ),
    user_progress: rawVideo?.user_progress
      ? {
          progress_seconds: toNumber(rawVideo.user_progress.progress_seconds, 0),
          is_completed: toBoolean(rawVideo.user_progress.is_completed),
          completed_at: rawVideo.user_progress.completed_at ?? null,
        }
      : null,
    playback: (() => {
      // API が署名付き sources を返す場合は最優先で使う
      if (explicitPlaybackSources.length > 0) {
        return {
          type: rawVideo?.playback?.type ?? 'progressive',
          url: resolveMediaUrl(explicitPlaybackUrl) ?? explicitPlaybackSources[0].url,
          sources: explicitPlaybackSources,
        };
      }
      // DB の mp4/webm キーがある場合はそれを最優先にする
      if (progressiveSources.length > 0) {
        return {
          type: 'progressive',
          url: primaryFromKeys,
          sources: progressiveSources,
        };
      }
      if (explicitPlaybackUrl) {
        const resolved = resolveMediaUrl(explicitPlaybackUrl);
        const type = rawVideo.playback.type;
        const isHls =
          type === 'hls' ||
          (resolved && String(resolved).toLowerCase().includes('.m3u8'));
        if (isHls) {
          return { ...rawVideo.playback, url: resolved };
        }
        return { ...rawVideo.playback, url: resolved };
      }
      if (fallbackPlaybackUrl) {
        return {
          type: 'mp4',
          url: resolveMediaUrl(fallbackPlaybackUrl),
        };
      }
      return null;
    })(),
    prevVideo: normalizeNavigationVideo(rawVideo?.prevVideo ?? rawVideo?.prev_video),
    nextVideo: normalizeNavigationVideo(rawVideo?.nextVideo ?? rawVideo?.next_video),
  };
}

export function normalizeSeries(rawSeries) {
  const totalDurationSeconds = toNumber(
    rawSeries?.totalDurationSeconds ?? rawSeries?.total_duration_seconds,
    0
  );

  return {
    id: rawSeries?.id,
    public_id: rawSeries?.publicId ?? rawSeries?.public_id ?? null,
    slug: rawSeries?.slug ?? null,
    title: rawSeries?.title ?? '',
    description: rawSeries?.description ?? '',
    genre: rawSeries?.genre ?? 'other',
    thumbnail_url: resolveMediaUrl(
      rawSeries?.thumbnailUrl ?? rawSeries?.thumbnail_url ?? rawSeries?.thumbnail_object_key ?? null
    ),
    total_videos: toNumber(rawSeries?.videoCount ?? rawSeries?.video_count ?? rawSeries?.total_videos, 0),
    total_duration_seconds: totalDurationSeconds,
    total_duration_minutes: totalDurationSeconds > 0
      ? Math.ceil(totalDurationSeconds / 60)
      : toNumber(rawSeries?.total_duration_minutes, 0),
    created_date: rawSeries?.createdAt ?? rawSeries?.created_at ?? null,
    is_published: rawSeries?.isPublished ?? rawSeries?.is_published ?? true,
  };
}

function normalizeDailyPostStatusItem(rawItem) {
  return {
    id: rawItem?.id ?? '',
    label: rawItem?.label ?? '',
    current: toNumber(rawItem?.current, 0),
    target: toNumber(rawItem?.target, 0),
    source_column: rawItem?.source_column ?? null,
  };
}

function normalizeMyProfileStats(rawStats) {
  return {
    stone: toNumber(rawStats?.stone, 0),
    level: rawStats?.level == null || rawStats?.level === '' ? null : toNumber(rawStats.level, 0),
    row_found: rawStats?.row_found === true,
  };
}

function normalizeDiscordReactionStats(rawStats) {
  const leaders = Array.isArray(rawStats?.leaders) ? rawStats.leaders : [];
  const emojis = Array.isArray(rawStats?.emojis) ? rawStats.emojis : [];

  return {
    days: toNumber(rawStats?.days, 30),
    since: rawStats?.since ?? null,
    leaders: leaders.map((leader) => ({
      id: leader?.discord_user_id ?? leader?.id ?? '',
      discord_user_id: leader?.discord_user_id ?? null,
      serverName: leader?.server_name ?? leader?.serverName ?? 'Unknown',
      reactionCount: toNumber(leader?.reaction_count ?? leader?.reactionCount, 0),
      lastReactedAt: leader?.last_reacted_at ?? leader?.lastReactedAt ?? null,
      avatarUrl: leader?.avatar_url ?? leader?.avatarUrl ?? null,
    })),
    emojis: emojis.map((emoji) => ({
      id: emoji?.emoji_id ?? emoji?.emoji_display ?? emoji?.emoji_name ?? 'unknown',
      emojiId:
        emoji?.emoji_id == null || String(emoji.emoji_id).trim() === ''
          ? null
          : String(emoji.emoji_id).trim(),
      emojiName: emoji?.emoji_name ?? null,
      emojiDisplay: emoji?.emoji_display ?? emoji?.emoji_name ?? 'unknown',
      reactionCount: toNumber(emoji?.reaction_count ?? emoji?.reactionCount, 0),
    })),
  };
}

function normalizeStampCardPointDay(rawItem) {
  const dateValue = rawItem?.date ?? rawItem?.awarded_date ?? rawItem?.awardedAt ?? rawItem?.awarded_at ?? null;
  const dayFromDate = dateValue ? Number(String(dateValue).slice(8, 10)) : NaN;

  return {
    date: dateValue,
    day: toNumber(rawItem?.day ?? rawItem?.day_of_month ?? dayFromDate, 0),
    pointAmount: toNumber(
      rawItem?.point_amount ??
        rawItem?.pointAmount ??
        rawItem?.total_points ??
        rawItem?.totalPoints ??
        rawItem?.points,
      0
    ),
  };
}

function normalizeDiscordPost(rawPost) {
  return {
    id: rawPost?.id ?? rawPost?.message_id ?? '',
    messageId: rawPost?.message_id ?? null,
    channelName: rawPost?.channel_name ?? null,
    channelId: rawPost?.channel_id ?? null,
    content: rawPost?.content ?? '',
    attachmentUrl: rawPost?.attachment_url ?? null,
    jumpUrl: rawPost?.jump_url ?? null,
    postedAt: rawPost?.posted_at ?? null,
    updatedAt: rawPost?.updated_at ?? null,
  };
}

/**
 * 公開側の動画一覧。`sort` に渡せる値は admin 一覧と共通:
 * `newest` / `oldest`（`created_at`）、`order_desc` / `order_asc`（`sort_order`）。
 * 未指定は `sort_order asc, id asc`。
 */
export async function listVideos(params = {}) {
  const response = await awsClient.get(`${contentBasePath}/videos`, {
    params,
  });
  const items = extractItems(response.data).map(normalizeVideo);

  return {
    items,
    pagination: response.data?.pagination ?? null,
  };
}

export async function listAllVideos(params = {}) {
  const pageSize = params.pageSize ?? 100;
  const firstPage = await listVideos({ ...params, page: 1, pageSize });
  const total = Number(firstPage.pagination?.total ?? firstPage.items.length);
  const totalPages = Math.ceil(total / pageSize);

  if (totalPages <= 1) return firstPage;

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      listVideos({ ...params, page: index + 2, pageSize })
    )
  );

  return {
    items: firstPage.items.concat(remainingPages.flatMap((page) => page.items)),
    pagination: firstPage.pagination,
  };
}

function buildCatalogSummaryFromVideos(videos) {
  const categoryMap = new Map();
  const tagMap = new Map();
  for (const video of videos) {
    const categoryKey = String(video.category_id ?? '');
    if (categoryKey) {
      const current = categoryMap.get(categoryKey) ?? {
        categoryId: video.category_id,
        total: 0,
        cover: video,
      };
      current.total += 1;
      categoryMap.set(categoryKey, current);
    }

    const rawTags = Array.isArray(video.tags) ? video.tags : [];
    rawTags.forEach((entry) => {
      const slug = typeof entry === 'string' ? entry : entry?.slug;
      const key = String(slug ?? '');
      if (key) tagMap.set(key, (tagMap.get(key) ?? 0) + 1);
    });
  }

  return {
    total: videos.length,
    categories: [...categoryMap.values()],
    tags: [...tagMap.entries()].map(([slug, total]) => ({ slug, total })),
  };
}

async function getCatalogSummaryFallback() {
  const fallback = await listAllVideos({ pageSize: 100, view: 'card' });
  return buildCatalogSummaryFromVideos(fallback.items);
}

export async function getCatalogSummary() {
  let response;
  try {
    response = await awsClient.get(`${contentBasePath}/catalog-summary`);
  } catch {
    return getCatalogSummaryFallback();
  }

  const categories = Array.isArray(response.data?.categories)
    ? response.data.categories.map((item) => ({
        categoryId: item.category_id ?? item.categoryId ?? item.id ?? null,
        total: toNumber(item.total ?? item.count ?? item.video_count ?? item.videoCount, 0),
        cover: item.cover ? normalizeVideo(item.cover) : null,
      }))
    : [];
  const tags = Array.isArray(response.data?.tags)
    ? response.data.tags.map((item) => ({
        slug: item.slug ?? item.value ?? item.tag ?? null,
        total: toNumber(item.total ?? item.count ?? item.video_count ?? item.videoCount, 0),
      }))
    : [];

  if (toNumber(response.data?.total, 0) > 0 && tags.length === 0) {
    return getCatalogSummaryFallback();
  }

  return {
    total: toNumber(response.data?.total, 0),
    categories,
    tags,
  };
}

export async function getVideoDetail(identifier) {
  const response = await awsClient.get(
    `${contentBasePath}/videos/${encodeURIComponent(identifier)}`
  );

  return normalizeVideo(response.data);
}

/**
 * 公開しているアーカイブサイトのオリジン。
 * テスト環境や dev サーバーから共有用URLを作っても**公開URLを返させる**ために、
 * 表示中のオリジン（`VITE_SITE_ORIGIN` / `window.location.origin`）とは別に固定値を持つ。
 * 差し替えたい場合だけ `VITE_ARCHIVE_SITE_ORIGIN` を設定する。
 */
const ARCHIVE_SITE_ORIGIN =
  (import.meta.env.VITE_ARCHIVE_SITE_ORIGIN ?? '').replace(/\/+$/, '') ||
  'https://illust-academia-archive.com';

function buildVideoPlayerUrl(origin, identifier) {
  if (!origin || identifier == null || identifier === '') return null;
  return `${origin}/#/VideoPlayer?id=${encodeURIComponent(identifier)}`;
}

/**
 * 共有用URL。**表示中のサイト**の VideoPlayer への直リンクを返す。
 * `identifier` は `videos.id` でも動画識別子（= `videos.slug`）でもよい。詳細APIが
 * どちらでも引けるので、識別子を使えば **アップロード受付の時点でURLが確定する**。
 * OGPカードは出ない（`#` 以降はサーバーに送られないため）。カードが必要なときは
 * `${VITE_API_BASE_URL}/content/videos/{identifier}/share` を使う。
 * @param {string|number|null|undefined} identifier
 */
export function getVideoShareUrl(identifier) {
  const origin =
    (import.meta.env.VITE_SITE_ORIGIN ?? '').replace(/\/+$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  return buildVideoPlayerUrl(origin, identifier);
}

/**
 * 共有用URL。**公開アーカイブサイト**の VideoPlayer への直リンクを返す。
 * オリジンを固定するので、テスト環境や dev サーバーで動画を登録しても
 * そのまま受講者へ渡せるURL（`https://illust-academia-archive.com/#/VideoPlayer?id=...`）になる。
 * admin の新規追加で表示する共有用URLはこちらを使う。
 * @param {string|number|null|undefined} identifier
 */
export function getArchiveVideoShareUrl(identifier) {
  return buildVideoPlayerUrl(ARCHIVE_SITE_ORIGIN, identifier);
}

/**
 * 再生数・視聴進捗の記録。`recordView: true` は「1再生として数える」用（初回再生などで1回だけ送る想定）
 * @param {string|number} identifier
 * @param {{ progress_seconds?: number, recordView?: boolean, forceCompleted?: boolean }} [payload]
 */
export async function postVideoView(identifier, payload = {}) {
  const body = {
    progress_seconds: Math.max(0, Math.floor(Number(payload.progress_seconds) || 0)),
    recordView: payload.recordView === true,
    forceCompleted: payload.forceCompleted === true,
  };
  await awsClient.post(
    `${contentBasePath}/videos/${encodeURIComponent(identifier)}/view`,
    body
  );
}

/**
 * ページ離脱時用。Authorization 付き fetch + keepalive（axios は keepalive 非対応のため）
 * @param {string|number} identifier
 * @param {{ progress_seconds?: number, recordView?: boolean, forceCompleted?: boolean }} [payload]
 */
export function postVideoViewKeepalive(identifier, payload = {}) {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  if (!base) return;

  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('aws_access_token') : null;
  const body = {
    progress_seconds: Math.max(0, Math.floor(Number(payload.progress_seconds) || 0)),
    recordView: payload.recordView === true,
    forceCompleted: payload.forceCompleted === true,
  };

  fetch(`${base}${contentBasePath}/videos/${encodeURIComponent(identifier)}/view`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
}

export async function listSeries() {
  try {
    const response = await awsClient.get(`${contentBasePath}/series`);
    return extractItems(response.data).map(normalizeSeries);
  } catch (error) {
    if (error?.response?.status === 404) {
      return [];
    }
    throw error;
  }
}

export async function listUserProgress() {
  const response = await awsClient.get(`${contentBasePath}/progress`);
  const items = extractItems(response.data);
  return items.map((p) => ({
    video_id: toNumber(p.video_id, 0),
    progress_seconds: toNumber(p.progress_seconds, 0),
    completed_at: p.completed_at ?? null,
    is_completed: toBoolean(p.is_completed),
    duration_seconds: toNumber(p.duration_seconds, 0),
    category_id: p.category_id ?? null,
  }));
}

export async function getDailyPostStatus() {
  const response = await awsClient.get(`${contentBasePath}/daily-post-status`);
  const items = extractItems(response.data).map(normalizeDailyPostStatusItem);

  return {
    items,
    total_current: toNumber(response.data?.total_current, 0),
    total_target: toNumber(response.data?.total_target, 0),
    completed_count: toNumber(response.data?.completed_count, 0),
    row_found: response.data?.row_found === true,
  };
}

export async function getMyProfileStats() {
  const response = await awsClient.get(`${contentBasePath}/daily-post-status`);
  return normalizeMyProfileStats(response.data?.profile ?? response.data);
}

export async function getDiscordReactionStats(params = {}) {
  const response = await awsClient.get(`${contentBasePath}/discord-reaction-stats`, {
    params,
  });
  return normalizeDiscordReactionStats(response.data);
}

export async function getStampCardPoints(params = {}) {
  const response = await awsClient.get(`${contentBasePath}/stamp-card-points`, {
    params,
  });
  return extractItems(response.data).map(normalizeStampCardPointDay);
}

export async function listMyDiscordPosts(params = {}) {
  const response = await awsClient.get(`${contentBasePath}/my-discord-posts`, {
    params,
  });
  return extractItems(response.data).map(normalizeDiscordPost);
}
